<template>
    <div
        ref="container"
        v-on-clickaway="handleClickaway"
        class="c-menu"
        :style="menuStyles"
    >
        <div
            ref="launcher"
            v-tab-focus.hasindex.hasrole.onspace="() => launchMenu()"
            class="c-menu__launcher-container"
            v-bind="launcherAttributes"
        >
            <slot name="launcher"></slot>
        </div>
        <div
            v-if="showPopover"
            ref="popover"
            class="c-menu__popover"
            :class="{
                'c-menu__popover--left': calculatedXPosition === 'left',
                'c-menu__popover--right': calculatedXPosition === 'right',
            }"
            :style="{
                '--popoverXOffset': `${popoverXOffset}px`,
                '--popoverYOffset': `${popoverYOffset}px`,
            }"
            role="tooltip"
        >
            <div class="c-menu__content">
                <div class="c-menu__body">
                    <component :is="menu.component" v-bind="$props.menuProps" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { uniqueId } from 'lodash-es';

import { baseMenu } from '~coreModules/core/js/provider-symbol-constants';
import { GLOBAL_MODALS } from '~coreModules/modals/js/modal-constants';
import { MODALS_MODULE_NAME, LAUNCH_MODAL } from '~coreModules/modals/js/modals-store';

export default {
    name: 'BaseMenu',
    directives: {
        ...(process.env.VUE_ENV === 'client' ? {
            // eslint-disable-next-line global-require
            onClickaway: require('vue3-click-away').directive,
        } : {
            onClickaway: {},
        }),
    },
    provide() {
        return {
            [baseMenu]: this.closePopover,
        };
    },
    props: {
        menu: {
            type: Object,
            required: true,
            validator(menu) {
                return (!!menu.component && !!menu.modalTitle);
            },
        },
        menuProps: {
            type: Object,
            default: () => ({}),
        },
        trigger: {
            type: String,
            default: 'click',
            validator(trigger) {
                // the programmatic option means that no user interaction can directly toggle the menu
                return ['click', 'programmatic'].includes(trigger);
            },
        },
        enableClickaway: {
            type: Boolean,
            default: true,
        },
        xPosition: {
            type: String,
            default: 'left',
        },
        popoverXOffset: {
            // this is the offset from the left or right edge of the launcher
            type: Number,
            default: 0,
        },
        popoverYOffset: {
            // this is the offset from the bottom of the launcher
            type: Number,
            default: 8, // default is $nu-spacer-1
        },
        isTabbable: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            showPopover: false,
            menuTopPosition: 0,
            launcherWidth: 0,
            calculatedXPosition: this.xPosition,
        };
    },
    computed: {
        uuid() {
            return uniqueId();
        },
        userCanTogglePopover() {
            return this.trigger !== 'programmatic';
        },
        tooltipHeaderId() {
            const { uuid } = this;
            return `tooltip-header-${uuid}`;
        },
        launcherAttributes() {
            return {
                tabindex: this.isTabbable ? '0' : '-1',
                role: 'button',
                'aria-haspopup': 'true',
                'aria-expanded': this.showPopover.toString(),
                'aria-labelledby': `${this.tooltipHeaderId}`,
            };
        },
        menuStyles() {
            return {
                '--menuTopPosition': `${this.menuTopPosition}px`,
                '--launcherWidth': `${this.launcherWidth}px`,
            };
        },
    },
    watch: {
        '$mediaQueries.isExtraSmall': function closePopOverOnSizeChange(isExtraSmall, wasExtraSmall) {
            if (isExtraSmall && !wasExtraSmall) {
                this.closePopover();
            }
        },
    },
    methods: {
        ...mapActions(MODALS_MODULE_NAME, {
            launchModal: LAUNCH_MODAL,
        }),
        launchMenu() {
            if (this.$mediaQueries.isExtraSmall) {
                this.launchModal({
                    ...GLOBAL_MODALS.baseMenuModal,
                    title: this.menu?.modalTitle,
                    styleType: 'BOTTOM-EXTRA-SHORT',
                    props: {
                        menuComponent: this.menu.component,
                        menuProps: this.menuProps,
                    },
                });
            } else {
                this.calculateMenuPositioning();
                this.showPopover = !this.showPopover;

                this.$nextTick(() => {
                    this.calculateXPosition();
                });
            }
        },
        closePopover() {
            if (this.userCanTogglePopover) {
                this.showPopover = false;
            }
        },
        handleClickaway() {
            if (this.enableClickaway && this.showPopover) {
                this.closePopover();
            }
        },
        focusCta() {
            this.$refs.cta.focus();
        },
        calculateMenuPositioning() {
            const launcherBoundingRect = this.$refs?.launcher?.getBoundingClientRect?.();
            this.menuTopPosition = launcherBoundingRect?.height || 0;
            this.launcherWidth = launcherBoundingRect?.width || 0;
        },
        calculateXPosition() {
            const popoverBoundingRect = this.$refs?.popover?.getBoundingClientRect();

            if (popoverBoundingRect.x < 0) {
                this.calculatedXPosition = 'left';
            }
            if (popoverBoundingRect.x + popoverBoundingRect.width > window.innerWidth) {
                this.calculatedXPosition = 'right';
            }
        },
    },
};
</script>

<style lang="scss">
    .c-menu {
        $this: &;
        position: relative;

        &__launcher-container {
            cursor: pointer;
        }

        &__popover {
            position: absolute;
            z-index: map-get($zindex, dropdown-menu);
            top: calc(var(--menuTopPosition) + var(--popoverYOffset));
            visibility: visible;
            border-color: transparent;
            border-style: solid;

            &--left {
                left: var(--popoverXOffset);
            }

            &--right {
                right: var(--popoverXOffset);
            }
        }

        &__content {
            @include dropshadow;
            position: relative;
            border-radius: 2px;
            background-color: $nu-white;
        }

        &__body {
            text-align: center;
            width: 240px;
        }
    }
</style>
